<template>
  <div class="checklist" :class="[typeClass, activeClass, completedClass, visibleClass]">
    <div class="checklist__wrapper">
      <!-- ---- CHECKLIST ----- -->
      <!-- CONTROL -->
      <ChecklistsChecklistControl :checklist="checklist" :isCompleted="isChecklistCompleted" />
      <!-- HEADING -->
      <ChecklistsChecklistHeading
        @title-clicked="showChecklist"
        @save-changes="updateChecklistValue"
        :checklist="checklist"
        :changes-saved="apiCallSucceed"
      />
      <!-- BODY -->
      <TransitionCollapse :time="400">
        <div v-if="visible || isChecklistActive" class="checklist__body">
          <!-- description -->
          <ChecklistsChecklistDescription
            :changesSaved="apiCallSucceed"
            @save-changes="updateChecklistValue"
            :checklist="checklist"
          />
          <!-- listing -->
          <div class="checklist__listing">
            <ChecklistsChecklistItem
              v-for="item in orderedItems"
              :id="`task-${item.id}`"
              :disabled="!isChecklistActive || isCardInactive"
              :key="item.id"
              :item="item"
              :data-id="item.id"
              :data-type="item.type"
            />
            <!-- next step  -->
            <transition name="scale-in">
              <ChecklistsChecklistNextStep
                v-if="isChecklistCompleted && isChecklistActive"
                v-allowed:employee
                :checklistId="checklist.id"
                @task-completed="closeChecklist"
              />
            </transition>
          </div>
          <!-- add item -->
          <ChecklistsAddAction
            is-item
            v-allowed:employee
            v-if="isEditable || !isTemplatePage"
            @added="addItem"
            text="Añadir item"
            keepEditBox
          />
        </div>
      </TransitionCollapse>
    </div>
  </div>
</template>

<script>
// Utilities
import ChecklistsMixin from '../mixins'
import { arraySortByObjectKey, ToNextElementOrder } from 'Utils'
import TransitionCollapse from 'Mixins/TransitionCollapse'
import ChecklistItemProto from '../classes/checklistPrototype'
// Components
import ChecklistsAddAction from './ChecklistsAddAction'
import ChecklistsChecklistItem from './ChecklistsChecklistItem'
import ChecklistsChecklistControl from './ChecklistsChecklistControl'
import ChecklistsChecklistHeading from './ChecklistsChecklistHeading'
import ChecklistsChecklistNextStep from './ChecklistsChecklistNextStep'
import ChecklistsChecklistDescription from './ChecklistsChecklistDescription'

export default {
  name: 'ChecklistsChecklist',

  components: {
    ChecklistsAddAction,
    ChecklistsChecklistItem,
    ChecklistsChecklistControl,
    ChecklistsChecklistHeading,
    ChecklistsChecklistNextStep,
    ChecklistsChecklistDescription,
  },

  props: {
    checklist: {
      type: Object,
      required: true,
    },

    transitionDuration: {
      type: Number,
      default: 400,
    },
  },

  mixins: [TransitionCollapse, ChecklistsMixin],

  data() {
    return {
      visible: false,
      order: '',
    }
  },

  methods: {
    closeChecklist() {
      this.visible = false
    },

    showChecklist() {
      if (this.userType === 'CLIENT' && !this.isChecklistCompleted) return
      this.toggleTransitionCollapse()
    },

    async setDraggingElements(element) {
      // check
      if (this.isDetailPage || !element || this.userType === 'CLIENT') return
      // preparing handler
      const handler = this.isEditPage
        ? '.checklist__control-icon--draggable'
        : '.checklist__item-heading'
      await this.$nextTick()
      const checklistItems = element.querySelector('.checklist__listing')
      // delay in case of cards pages
      if (!this.isTemplatePage) this.$Sortable.checklistItem.config.delay = 200
      this.$Sortable.checklistItem.create(checklistItems, handler)
    },

    // updates
    async updateChecklistValue(newValues) {
      const update = await this.$store.dispatch('checklists/updateChecklist', newValues)
      if (!this.checkApiResponse(update)) return
      this.changeSavedToggle()
    },

    async addItem(values) {
      // ADDING NEW ITEM
      const newItem = this.getChecklistData({
        checklistId: this.checklist.id,
        itemValues: new ChecklistItemProto({
          title: values.title,
          // type: values.type,
          order: ToNextElementOrder(this.orderedItems[this.orderedItems.length - 1]?.order),
        }),
      })
      await this.$store.dispatch('checklists/addItem', newItem)
    },
  },

  computed: {
    orderedItems() {
      return arraySortByObjectKey(this.checklist.items, 'order')
    },

    typeClass() {
      return this.isTemplatePage && 'template'
    },

    activeClass() {
      return this.checklist.is_active ? 'active' : 'locked'
    },

    visibleClass() {
      return (this.visible || this.isChecklistActive) && 'visible'
    },

    completedClass() {
      return this.isChecklistCompleted && !this.isTemplatePage && 'completed'
    },

    isChecklistActive() {
      return this.checklist.is_active
    },

    isChecklistCompleted() {
      return this.$store.getters['checklists/isChecklistCompleted'](this.checklist.id)
    },

    isCardInactive() {
      return this.$store.state.cards.currentCard.status !== 'active'
    },
  },

  beforeMount() {
    this.visible = this.isTemplatePage || this.checklist.is_active ? true : false
  },
  // DRAGG AND DROP CONFIG
  mounted() {
    if (this.isDetailPage || !this.$parent.$el) return
    // setting up checklists draggable
    window.checklistDraggable && this.$Sortable.checklist.destroy(window.checklistDraggable)
    window.checklistDraggable = this.$Sortable.checklist.create(
      this.$parent.$el.firstChild,
      '.checklist__control-icon'
    )
  },

  watch: {
    // Setting up items for drag&drop
    visible(newValue) {
      if (newValue) {
        this.setDraggingElements(this.$el)
      }
    },

    isChecklistActive(newVal) {
      if (!newVal) this.visible = false
      if (newVal) this.visible = true
    },
  },
}
</script>

<style scoped>
/* basics */
.checklist {
  position: relative;
  margin: 20px 0;
}

.checklist .checklist__wrapper {
  word-break: break-word;
}

.checklist.template .checklist__wrapper {
  padding: 10px 20px;
}

.checklist:not(.template) .checklist__wrapper {
  display: grid;
  grid-template-columns: 36px 1fr;
  column-gap: 8px;
  position: inherit;
}

.checklist:not(.template).progress-bar .checklist__wrapper:before,
.checklist:not(.template).progress-bar .checklist__wrapper:after {
  position: absolute;
  content: '';
  top: 16px;
  left: 9px;
  width: 2px;
}

.progress-bar .checklist__wrapper:before {
  height: 0;
  background-color: var(--c-primary);
  transition: var(--transition--400);
  z-index: 2;
}

.completed.progress-bar .checklist__wrapper:before {
  height: calc(100% + 30px);
}

.progress-bar .checklist__wrapper:after {
  height: calc(100% + 30px);
  background-color: var(--c-secondary-500);
  z-index: 1;
}

.checklist__body {
  grid-column: 2;
  padding-left: 8px;
  padding-right: 8px;
  margin: 0 0 0 -5px;
}
/* template */
.checklist.template {
  border-radius: 4px;
  border-left: 5px solid var(--c-secondary-500);
  box-shadow: var(--box-shadow-default);
  margin: 0 0 20px;
}

/* default */
.checklist__listing {
  display: flex;
  flex-flow: column;
  margin-top: 15px;
}
</style>
